/**
 * CallFoundry Local Audio Storage Policy (Milestone M4)
 * 
 * Enforces privacy, zero-cloud sync, and strict local storage quotas.
 * Practice audio recordings are ephemeral (in-memory) by default.
 * If retained locally, storage is capped at MAX_SAVED_RECORDINGS (10) or
 * MAX_STORAGE_BYTES (25 MB) with automatic FIFO eviction of the oldest recordings.
 */

export interface SavedRecordingMetadata {
  id: string;
  exerciseId: string;
  recordedAt: number;
  durationMs: number;
  mimeType: string;
  sizeBytes: number;
}

export const AUDIO_STORAGE_POLICY = {
  MAX_SAVED_RECORDINGS: 10,
  MAX_STORAGE_BYTES: 25 * 1024 * 1024, // 25 MB
  DEFAULT_PERSIST_RECORDINGS: false,
} as const;

export class LocalAudioStoragePolicy {
  /**
   * Evaluates if a new recording can be safely stored within the quota.
   * If budget is exceeded, returns list of recording IDs that should be evicted.
   */
  public getEvictionCandidates(
    currentRecordings: SavedRecordingMetadata[],
    incomingBytes: number
  ): string[] {
    const sorted = [...currentRecordings].sort((a, b) => a.recordedAt - b.recordedAt);
    const toEvict: string[] = [];

    let totalCount = sorted.length + 1;
    let totalBytes = sorted.reduce((acc, r) => acc + r.sizeBytes, 0) + incomingBytes;

    while (
      sorted.length > 0 &&
      (totalCount > AUDIO_STORAGE_POLICY.MAX_SAVED_RECORDINGS ||
        totalBytes > AUDIO_STORAGE_POLICY.MAX_STORAGE_BYTES)
    ) {
      const oldest = sorted.shift();
      if (oldest) {
        toEvict.push(oldest.id);
        totalCount--;
        totalBytes -= oldest.sizeBytes;
      }
    }

    return toEvict;
  }

  /**
   * Queries browser StorageManager for overall IndexedDB usage estimate.
   */
  public async getStorageEstimate(): Promise<{ usageBytes: number; quotaBytes: number; percentUsed: number }> {
    if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
      try {
        const estimate = await navigator.storage.estimate();
        const usage = estimate.usage || 0;
        const quota = estimate.quota || 1;
        return {
          usageBytes: usage,
          quotaBytes: quota,
          percentUsed: Math.round((usage / quota) * 100),
        };
      } catch (err) {
        console.warn('[AudioStoragePolicy] Could not read storage estimate:', err);
      }
    }

    return {
      usageBytes: 0,
      quotaBytes: AUDIO_STORAGE_POLICY.MAX_STORAGE_BYTES,
      percentUsed: 0,
    };
  }
}

export const audioStoragePolicy = new LocalAudioStoragePolicy();
